import { Datagrid } from "components/CustomizableDataGrid";
import { AppList } from "components/list/AppList";
import React from "react";
import {
	BooleanField,
	FunctionField,
	Loading,
	NumberField,
	TextField,
	usePermissions,
} from "react-admin";
import { COLD_STORAGE_ARTICLE_CODES, plainNumber } from "utils/constants";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";

import { DateTimeField } from "../../../components/fields/DateFormatField";
import { EnergyAwarePriceField } from "../../../components/fields/EnergyAwarePriceField";
import { OrderReferenceField } from "../../../components/fields/OrderReferenceField";
import { PriceField } from "../../../components/fields/PriceField";
import { SelectEnumField } from "../../../components/fields/SelectEnumField";
import { UserFullNameReferenceField } from "../../../components/fields/UserFullNameReferenceField";
import { ScrollingWrapper } from "../../../components/ScrollingWrapper";
import { useGridUserDataGlobalFiltersContext } from "../../../contexts/GridUserDataGlobalFiltersProvider";
import { useUserSettingsContext } from "../../../contexts/UserSettingsProvider";
import { AppManagerRoles, UserRole } from "../../../entities";
import {
	ChargeTypeByCode,
	CustomerServiceChargeType,
} from "../../../entities/CustomerCDChargeType";
import { CustomerArticlesActions } from "./Actions";
import { AsOfDateModeProvider, useAsOfDateMode } from "./AsOfDateModeContext";
import { CustomerArticlesListFilters } from "./ListFilters";

const CustomerArticlesListContent = (props) => {
	const { permissions } = usePermissions();
	const { data: userSettings } = useUserSettingsContext();
	const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();
	const userRole = permissions?.role;
	const { isAsOfDateMode } = useAsOfDateMode();
	const columnsAsyncStorage = useAsyncColumnsStorage("customer-articles-columns-storage");

	const canSeeAllCompanies = AppManagerRoles.includes(userRole);
	const isAdmin = userRole === UserRole.admin;

	const currency = userSettings?.company_settings?.currency;
	const terminalId = globalFilters.terminal_id?.[0];

	const filter = {
		"article_code:in": COLD_STORAGE_ARTICLE_CODES,
		...(currency ? { currency } : {}),
		...(terminalId ? { terminal_id: terminalId } : {}),
	};

	if (!canSeeAllCompanies && !currency) {
		return <Loading loadingSecondary="" />;
	}

	return (
		<AppList
			{...props}
			filters={
				<CustomerArticlesListFilters isAdmin={isAdmin} canFilterByCompany={canSeeAllCompanies} />
			}
			filter={filter}
			sort={{ field: "created_at", order: "DESC" }}
			perPage={isAsOfDateMode ? 2000 : undefined}
			pagination={isAsOfDateMode ? false : undefined}
			actions={<CustomerArticlesActions />}
			bulkActionButtons={false}
			empty={false}
		>
			<ScrollingWrapper>
				<Datagrid
					rowClick={isAsOfDateMode ? false : "show"}
					storage={columnsAsyncStorage}
					reorderingSupport={true}
				>
					<TextField source="id" />

					{canSeeAllCompanies && <TextField source="company_name" />}
					<TextField source="company_accountancy_number" />

					<NumberField source="article_code" {...plainNumber} />
					<TextField source="name" />
					<NumberField source="units" options={{ minimumFractionDigits: 0 }} />
					<BooleanField source="is_t1" />
					<OrderReferenceField source="order_id" />
					<TextField source="ceel_number" />
					<TextField source="batch_number" />
					<FunctionField
						source="article_code"
						reorder_name="charge_type"
						label="resources.customer-articles.fields.charge_type"
						render={(record) => (
							<SelectEnumField
								record={{ charge_type: ChargeTypeByCode[record.article_code] }}
								source="charge_type"
								enumObj={CustomerServiceChargeType}
								enumName="cs_service_charge_type"
							/>
						)}
					/>
					{currency ? (
						<EnergyAwarePriceField source="price" currency={currency} />
					) : (
						<NumberField source="price" options={{ style: "currency", currency: "NOK" }} />
					)}
					{currency ? (
						<PriceField source="total_amount" mainCurrency={currency} />
					) : (
						<NumberField source="total_amount" options={{ style: "currency", currency: "NOK" }} />
					)}
					<DateTimeField source="created_at" />
					<DateTimeField source="updated_at" />
					<UserFullNameReferenceField source="updated_by" />
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};

export const CustomerArticlesList = (props) => (
	<AsOfDateModeProvider>
		<CustomerArticlesListContent {...props} />
	</AsOfDateModeProvider>
);
